import { GetServerSideProps, GetServerSidePropsContext, PreviewData } from 'next/types';
import Head from 'next/head';
import { ParsedUrlQuery } from 'querystring';

import { metrics } from '@/src/homepage/common';
import { getRequestContext, withPublicSurfaceUrl } from '@/src/homepage/utils/requestContext';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getGlobalFooterServerSideProps } from '@eventbrite/legacy-infra.site-footer/server';
import { useIntl } from 'react-intl';
import { EventbriteHeader, stageFromString, Stage } from '@eventbrite/webapp-constants';
import { OpenGraphTags, TwitterTags } from '@eventbrite/webapp-public-content';
import { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { getHreflangLinks, isDualLanguageTLD } from './localeConfig';
import { loadI18nProps } from '@/src/homepage/utils/i18n';
import { createDefaultLocaleInfo } from '@/src/homepage/utils/localeInfo';
import { getGuides } from '@/src/homepage/api/guides/temp/getGuides';
import { GUIDE_MARKETS_BY_LOCATION_SLUG } from '@/src/homepage/api/guides/temp/hydrateGuideLocation';
import type { Guides } from '@/src/homepage/types/guides';
import { getUserLocationFromRequest } from '@/src/homepage/common';
import { getServerSideSplitValues, type SplitValues } from '@/src/homepage/utils/split';
import { minimalInitialState } from '@/src/homepage/redux';
import type { BaseEnv, Location } from '@/src/homepage/types';
import { setHomepageDocumentCacheControl } from '@/src/homepage/utils/documentCache';

/**
 * Server data the host `_app` used to provide via its (removed) homepage
 * `getInitialProps`: the Split feature values and the serialized initial Redux
 * state. Each homepage page's `getServerSideProps` now returns these so the
 * `HomepageLayout` can mount the same providers the standalone `_app` did.
 */
export async function getHomepageShellData(input: {
    env: BaseEnv;
    location?: Location;
}): Promise<{ splitValues: SplitValues; initialState: ReturnType<typeof minimalInitialState> }> {
    const splitValues = await getServerSideSplitValues();
    const initialState = minimalInitialState({
        env: input.env,
        ...(input.location && { location: input.location }),
    });
    return { splitValues, initialState };
}

const _getRequestId = (context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>) =>
    (context.req?.headers[EventbriteHeader.AMZN_REQUESTID] as string) || 'MISSING_REQUEST_ID';

const handleRejectedResult = (result: PromiseSettledResult<unknown>, operationName: string) => {
    if (result.status === 'rejected') {
        console.error(`Failed to ${operationName}:`, result.reason);
    }
};

interface HomepageHeadProps {
    serverUrl: string;
    stage: Stage;
}

/**
 * Shared Head component for homepage with hreflang tags
 */
export function HomepageHead({ serverUrl, stage }: HomepageHeadProps) {
    const intl = useIntl();
    const pageTitle = intl.formatMessage({
        id: 'Eventbrite - Discover the Best Local Events & Things to Do',
        defaultMessage: 'Eventbrite - Discover the Best Local Events & Things to Do',
    });
    const pageDescription = intl.formatMessage({
        id: 'Find tickets to your next unforgettable experience. Browse concerts, workshops, yoga classes, charity events, food and music festivals, and more things to do.',
        defaultMessage:
            'Find tickets to your next unforgettable experience. Browse concerts, workshops, yoga classes, charity events, food and music festivals, and more things to do.',
    });

    const hreflangLinks = getHreflangLinks(stage);

    return (
        <Head>
            <title>{String(pageTitle)}</title>
            <link rel="canonical" href={serverUrl} />
            {hreflangLinks.map((link, index) => (
                <link key={index} rel="alternate" href={link.href} hrefLang={link.hreflang} />
            ))}
            <OpenGraphTags url={serverUrl} description={String(pageDescription)} />
            <TwitterTags description={String(pageTitle)} />
        </Head>
    );
}

/**
 * Shared getServerSideProps logic for homepage routes
 * @param localePathParam - Optional locale from URL path (e.g., 'fr_CA')
 */
export function getHomepageServerSideProps(localePathParam?: string): GetServerSideProps {
    return async (context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>) => {
        const baseTags = {
            requestId: _getRequestId(context),
        };
        const publicUserId = (context.req?.headers[EventbriteHeader.EB_USER_ID] as string) || '';
        const isUserAuthenticated = !!publicUserId;
        const stage = stageFromString(process.env.STAGE || Stage.TEST);
        const { serverUrl, tld, host, locale } = getRequestContext(context.req);
        const ebDomain = host.replace(/^www\./, '');

        const effectiveLocale = localePathParam || locale || '';
        const localeInfo = createDefaultLocaleInfo(context.req);
        const i18nProps = await loadI18nProps(effectiveLocale);
        // The header/footer libraries read `req.url` for `path`/`isHomePage`;
        // the standalone saw it with the `/home` basePath already stripped, so
        // hand them the mount-stripped context to keep `/` and `/fr_CA` shapes.
        const publicContext = withPublicSurfaceUrl(context);
        const [globalHeaderResult, globalFooterResult] = await Promise.allSettled([
            metrics.measure(
                () => getGlobalHeaderServerSideProps(publicContext),
                'getGlobalHeaderServerSideProps',
                { ...baseTags, loggedIn: String(isUserAuthenticated) },
            ),
            metrics.measure(
                () => getGlobalFooterServerSideProps(publicContext),
                'getGlobalFooterServerSideProps',
                { ...baseTags, loggedIn: String(isUserAuthenticated) },
            ),
        ]);

        handleRejectedResult(globalHeaderResult, 'fetch global header');
        handleRejectedResult(globalFooterResult, 'fetch global footer');

        const publicPath = publicContext.req.url;
        const emptyGlobalHeader: GlobalHeaderProps = {
            env: {
                serverUrl: serverUrl,
            },
            request: {
                path: publicPath,
                session_id: context.req.headers[EventbriteHeader.EB_SESSION_ID] as string,
            },
            isHomePage:
                publicPath == '' ||
                publicPath == '/' ||
                publicPath?.startsWith('/home'),
        };

        const emptyGlobalFooter = {
            thirdFooterColumnName: 'Find Events',
            thirdFooterColumn: [],
            popularSearches: [],
            eventsToPlan: [],
            showMinimalLinks: false,
            impressumHelpLink: '',
        };

        const globalHeaderProps =
            globalHeaderResult.status === 'fulfilled'
                ? globalHeaderResult.value
                : emptyGlobalHeader;

        const globalFooterProps =
            globalFooterResult.status === 'fulfilled'
                ? {
                      ...globalFooterResult.value,
                      thirdFooterColumn: globalFooterResult.value.thirdFooterColumn ?? [],
                      eventsToPlan: globalFooterResult.value.eventsToPlan ?? [],
                      popularSearches: globalFooterResult.value.popularSearches ?? [],
                  }
                : emptyGlobalFooter;

        const footerProps = {
            serverUrl: serverUrl,
            localeInfo: localeInfo,
            isUserAuthenticated: isUserAuthenticated,
            globalFooterProps,
        };

        // Check if eb-location-slug header is present and in the guides dictionary
        const locationSlug = context.req?.headers['eb-location-slug'] as string | undefined;
        let guides: Guides | undefined;

        if (locationSlug && GUIDE_MARKETS_BY_LOCATION_SLUG[locationSlug]) {
            try {
                console.debug('Loading guides for location slug:', locationSlug);
                guides = await metrics.measure(
                    () => getGuides(context.req, { slug: locationSlug }),
                    'getGuides',
                    { ...baseTags, locationSlug },
                );
                console.debug('Guides loaded successfully:', {
                    locationSlug,
                    guidesCount: guides?.items?.length || 0,
                });
            } catch (error) {
                console.error('Error loading guides for location slug:', locationSlug, error);
                // Continue without guides if there's an error
            }
        }

        // `?debug=true` requests render the debug banner. The homepage document
        // is shared-cached under a key that excludes query strings, so a debug
        // response must never be stored and served to non-debug viewers; keep
        // the normal public caching for every other request.
        const debug = context.query?.debug === 'true';

        // For dual-language TLDs, remove browser cache (max-age=0) while keeping CloudFront cache
        // For other TLDs, keep standard caching behavior
        // this is to avoid the browser caching the wrong locale for dual-language TLDs
        const CACHE_CONTROL = isDualLanguageTLD(tld)
            ? 'max-age=0, s-maxage=600, stale-while-revalidate=3600, stale-if-error=86400'
            : 'max-age=300, s-maxage=600, stale-while-revalidate=3600, stale-if-error=86400';

        // Fetch user location on the server so it's available before first paint.
        // Normalize to null so getServerSideProps return value is JSON-serializable (no undefined).
        let location: Record<string, string | number | boolean | null> | undefined;
        try {
            const place = await metrics.measure(
                () => getUserLocationFromRequest(context.req),
                'getUserLocationFromRequest',
                baseTags,
            );
            if (place) {
                location = {
                    slug: place.slug ?? null,
                    placeId: place.placeId ?? null,
                    latitude: place.latitude ?? null,
                    longitude: place.longitude ?? null,
                    currentPlace: place.currentPlace ?? null,
                    currentPlaceParent: place.currentPlaceParent ?? null,
                    placeType: place.placeType ?? null,
                    isOnline: place.isOnline ?? null,
                };
            }
        } catch (error) {
            console.error('Error fetching user location in getServerSideProps:', error);
        }

        const env = {
            serverUrl,
            tld,
            ebDomain,
            localeInfo,
        };
        const { splitValues, initialState } = await getHomepageShellData({
            env: env as unknown as BaseEnv,
            ...(location && { location: location as unknown as Location }),
        });

        setHomepageDocumentCacheControl(context, CACHE_CONTROL);

        return {
            props: {
                stage,
                publicUserId,
                env,
                globalHeaderProps,
                footerProps,
                i18nProps,
                isHomepageRoute: true,
                splitValues,
                initialState,
                debug,
                ...(location && { location }),
                ...(guides && { guides }),
            },
        };
    };
}
